<template>
    <div class="pb-10">
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('key_facts') }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <div v-if="isCruiseForm" class="text-link pb-2" @click="isShowModal = true">
                    {{ $t('associate_with_ship') }}
                    {{ contract.shipContentId && `: ${currentShipName}` }}
                    <v-icon>mdi-link-variant</v-icon>
                </div>
                <v-checkbox
                    v-model="contract.theSameSecondaryServiceForAll"
                    hide-details
                    :label="$t('same_board_type')"
                    density="compact"
                    class="pb-6"
                    data-cy="same-board-type"
                />
                <div v-if="isCruiseForm" class="text-subtitle-1 font-weight-medium pb-2">
                    {{ $t('starting_location') }}
                </div>
                <v-row v-if="isCruiseForm" dense class="pb-6">
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="contract.departureLocations[0].countryId"
                            :items="useCountries"
                            item-title="title"
                            item-value="value"
                            :label="$t('country')"
                            closable-chips
                            hide-details
                            clearable
                            density="compact"
                            color="primary"
                            data-cy="departure-country-id"
                            @update:model-value="updateDepartureLocations()"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            ref="cityRef"
                            v-model="contract.departureLocations[0].cityId"
                            :items="cities"
                            item-title="title"
                            item-value="value"
                            :label="$t('city')"
                            :loading="isCitiesLoading"
                            :disabled="isCitiesLoading || !cities.length"
                            closable-chips
                            hide-details
                            clearable
                            density="compact"
                            color="primary"
                            data-cy="departure-city-id"
                            :rules="contract.departureLocations[0].countryId ? [requiredRule('city')] : []"
                        />
                    </v-col>
                </v-row>
                <div class="text-subtitle-1 font-weight-medium">{{ $t('ending_location') }}</div>
                <div class="text-grey pb-2 text-subtitle-2 font-weight-regular">
                    {{ isCruiseForm ? $t('destination_cruise_note') : $t('destination_tour_note') }}
                </div>
                <div
                    v-if="isShowValidationError && !contract.destinationLocations?.length"
                    class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
                >
                    {{ `${$t('ending_location')} ${$t('validation.required')}` }}
                </div>
                <v-row dense>
                    <v-col cols="12" md="8" class="pb-8">
                        <InfoLocationForm v-model="contract.destinationLocations" />
                    </v-col>
                    <v-col cols="12" md="8">
                        <v-row dense>
                            <v-col class="flex-grow-0">
                                <v-text-field
                                    v-model="contract.duration"
                                    :label="`${$t('duration')}*`"
                                    type="number"
                                    hide-spin-buttons
                                    variant="outlined"
                                    color="primary"
                                    density="compact"
                                    data-cy="duration"
                                    maxlength="5"
                                    hide-details
                                    width="120px"
                                    :rules="[requiredRule('duration')]"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <ModalWrapper v-model="isShowModal" :max-width="1200">
            <template v-slot:title>
                {{ $t('associate_with_ship') }}
            </template>
            <template v-slot:content>
                <ShipItems @associate-ship="associateShip" />
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {useCountries} from '~/composables/form-helpers'
    import {contractServiceType, type SelectOptionType} from '~/utils/types/types'
    import InfoLocationForm from '~/components/products/information/InfoLocationForm.vue'
    import ShipItems from '~/components/products/cruises/ShipItems.vue'

    const props = defineProps<{
        serviceType: string
    }>()

    const emit = defineEmits<{
        (e: 'validate'): void
    }>()

    const isCitiesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()
    const isShowModal = ref<boolean>(false)
    const currentShipName = ref<string>(null)
    const isCruiseForm = ref<boolean>(props.serviceType === contractServiceType.ownCruise)
    const isShowValidationError = inject('isShowValidationError', ref(false))

    const contract = defineModel<any>()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (contract.value?.shipContentId) {
            await loadShips()
        }
        if (contract.value?.departureLocations && contract.value?.departureLocations[0]?.cityId) {
            cities.value = await loadCities(contract.value.departureLocations[0].countryId)
        }
    })

    if (isCruiseForm.value) {
        watch(useCountries, async () => {
            const countryId = contract.value.departureLocations[0]?.countryId
            if (countryId) cities.value = await loadCities(countryId)
        })
    }

    async function loadShips() {
        try {
            const {
                shipContents: [_shipContents],
            } = await app.$api.shipContent.get({id: contract.value.shipContentId})

            currentShipName.value = _shipContents?.name || `(${t('id')} ${contract.value?.shipContentId})`
        } catch (e) {
            currentShipName.value = null
        }
    }

    const updateDepartureLocations = async () => {
        if (contract.value?.departureLocations[0]?.cityId) cityRef.value.reset()
        const countryId = contract.value.departureLocations[0].countryId
        if (countryId) emit('validate')
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        countryId ? (cities.value = await loadCities(countryId)) : (cities.value = [])
    }

    const associateShip = (ship): void => {
        const {id, name} = ship
        contract.value.shipContentId = id
        currentShipName.value = name
        isShowModal.value = false
    }

    async function loadCities(countryId: number) {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: countryId,
            })
            if (rs.cities) {
                return rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            return []
        } finally {
            isCitiesLoading.value = false
        }
    }
</script>
